import React, { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { api, formatErr } from "@/lib/api";

export default function Contact() {
  const [form, setForm] = useState({customerName:"", email:"", phone:"", specialRequirements:""});
  const [submitting, setSubmitting] = useState(false);
  const set = (k,v) => setForm(f => ({...f, [k]: v}));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/inquiries", {
        customerName: form.customerName,
        email: form.email,
        phone: form.phone,
        travelerCount: 1,
        preferredDepartureDate: null,
        specialRequirements: form.specialRequirements,
      });
      toast.success("Message sent! We'll get back to you shortly.");
      setForm({customerName:"", email:"", phone:"", specialRequirements:""});
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Something went wrong");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="bg-ivory">
      <div className="bg-maroon text-amber-50 py-14 relative overflow-hidden">
        <div className="absolute inset-0 grain pointer-events-none opacity-30" />
        <div className="relative max-w-7xl mx-auto px-4">
          <div className="text-amber-300 text-sm font-semibold tracking-widest uppercase mb-3">Get in touch</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight">Contact Us</h1>
          <p className="mt-4 text-amber-50/90 text-lg max-w-2xl">Our travel specialists respond within 24 hours — in Bengali, Hindi or English. Prefer to talk? Give us a call.</p>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 py-14 grid lg:grid-cols-2 gap-10">
        <div className="space-y-6">
          <div className="card-warm p-7 flex gap-5">
            <span className="w-14 h-14 rounded-full bg-amber-100 text-amber-700 grid place-items-center shrink-0"><Phone size={24}/></span>
            <div>
              <div className="font-display text-lg font-bold text-maroon mb-1">Phone</div>
              <div className="text-slate-700 space-y-1">
                <a href="tel:+919874250100" className="block hover:text-amber-700">+91 98742 50100</a>
                <a href="tel:+919433050100" className="block hover:text-amber-700">+91 94330 50100</a>
                <a href="tel:+919836330100" className="block hover:text-amber-700">+91 98363 30100</a>
              </div>
            </div>
          </div>
          <div className="card-warm p-7 flex gap-5">
            <span className="w-14 h-14 rounded-full bg-amber-100 text-amber-700 grid place-items-center shrink-0"><Mail size={24}/></span>
            <div>
              <div className="font-display text-lg font-bold text-maroon mb-1">Email</div>
              <a href="mailto:welcome@globalcompass.in" className="text-slate-700 hover:text-amber-700">welcome@globalcompass.in</a>
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden border border-amber-900/10 shadow-lg" data-testid="contact-map">
            <iframe
              title="Global Compass office location"
              src="https://www.google.com/maps?q=70C%20Sadananda%20Road%2C%203rd%20Floor%2C%20Kolkata%20700026%2C%20West%20Bengal&output=embed"
              width="100%"
              height="300"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
        <form onSubmit={submit} className="card-warm p-8 space-y-4" data-testid="contact-form">
          <h2 className="font-display text-2xl font-bold text-maroon">Send us a message</h2>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Full Name *</label>
            <input required value={form.customerName} onChange={(e)=>set("customerName",e.target.value)}
              className="w-full h-12 px-4 rounded-lg border-2 border-amber-900/15 focus:border-amber-600 focus:outline-none bg-ivory text-base" data-testid="contact-name"/>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Email *</label>
              <input required type="email" value={form.email} onChange={(e)=>set("email",e.target.value)}
                className="w-full h-12 px-4 rounded-lg border-2 border-amber-900/15 focus:border-amber-600 focus:outline-none bg-ivory text-base" data-testid="contact-email"/>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Phone *</label>
              <input required value={form.phone} onChange={(e)=>set("phone",e.target.value)}
                className="w-full h-12 px-4 rounded-lg border-2 border-amber-900/15 focus:border-amber-600 focus:outline-none bg-ivory text-base" data-testid="contact-phone"/>
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Message</label>
            <textarea rows={5} value={form.specialRequirements} onChange={(e)=>set("specialRequirements",e.target.value)}
              className="w-full px-4 py-3 rounded-lg border-2 border-amber-900/15 focus:border-amber-600 focus:outline-none bg-ivory text-base" data-testid="contact-message"/>
          </div>
          <button disabled={submitting} className="btn-gold w-full h-14 rounded-full font-bold text-lg disabled:opacity-60" data-testid="contact-submit">
            {submitting ? "Sending…" : "Send Message"}
          </button>
        </form>
      </div>
      <div className="max-w-7xl mx-auto px-4 pb-16 grid md:grid-cols-2 gap-6">
        <div className="card-warm p-7 flex gap-5" data-testid="contact-address">
          <span className="w-14 h-14 rounded-full bg-amber-100 text-amber-700 grid place-items-center shrink-0"><MapPin size={24}/></span>
          <div>
            <div className="font-display text-lg font-bold text-maroon mb-1">Reach Us</div>
            <div className="text-slate-700">70C Sadananda Road, 3rd Floor,<br/>Kolkata 700026, West Bengal, India</div>
          </div>
        </div>
        <div className="card-warm p-7 flex gap-5" data-testid="contact-hours">
          <span className="w-14 h-14 rounded-full bg-amber-100 text-amber-700 grid place-items-center shrink-0"><Clock size={24}/></span>
          <div>
            <div className="font-display text-lg font-bold text-maroon mb-1">Office Hours</div>
            <div className="text-slate-700">Monday – Saturday, 10:00 AM – 7:00 PM IST</div>
          </div>
        </div>
      </div>
    </div>
  );
}
